import type { ReactNode } from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { isFollowUpVisible, isQuestionVisible } from "@kmvs/forms-core";
import { BRAND } from "./tokens";
import type { Answers, QuestionDef } from "./types";
import QuestionField from "./QuestionField";

const useStyles = makeStyles({
  block: {
    paddingTop: "20px",
    paddingBottom: "20px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  question: {
    marginTop: 0,
    marginBottom: "4px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    fontWeight: 400,
    lineHeight: 1.3,
    color: BRAND.ink,
    "@media (min-width: 768px)": {
      fontSize: "1.35rem",
    },
  },
  hint: {
    marginTop: 0,
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.muted,
  },
  body: {
    display: "grid",
    gap: "14px",
    gridTemplateColumns: "1fr",
    "& > *": { minWidth: 0 },
  },
  bodyTwo: {
    display: "grid",
    gap: "14px",
    gridTemplateColumns: "1fr",
    "& > *": { minWidth: 0 },
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
      columnGap: "20px",
    },
  },
  slot: {
    marginTop: "12px",
  },
  reveal: {
    marginTop: "12px",
    paddingLeft: "14px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingRight: "14px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    display: "grid",
    gap: "12px",
    "& > *": { minWidth: 0 },
  },
});

type Props = {
  question: QuestionDef;
  answers: Answers;
  onChange: (key: string, value: unknown) => void;
  /** Optional set of field keys whose value was filled from a Fahrzeugschein
   *  OCR pass — used to render a small chip next to the field label. */
  ocrFilledKeys?: ReadonlySet<string>;
  /**
   * Eigener Inhalt, der fachlich **zu dieser Frage** gehört, aber zu reich für
   * `followUpFields` ist (Listen mit mehreren Feldern je Zeile, eigene
   * Auswahlblöcke). Er steht innerhalb des Frageblocks — sonst legte sich der
   * gestrichelte Trennstrich zwischen Frage und Antwortteil und der Inhalt läse
   * sich als Vorspann der nächsten Frage. Der Aufrufer entscheidet selbst, ob
   * er ihn abhängig von der Antwort übergibt.
   */
  children?: ReactNode;
};

const QuestionBlock = ({
  question,
  answers,
  onChange,
  ocrFilledKeys,
  children,
}: Props) => {
  const styles = useStyles();
  if (!isQuestionVisible(question, answers)) return null;

  const fields = question.fields ?? (question.field ? [question.field] : []);
  const showFollowUp = isFollowUpVisible(question, answers);

  return (
    <section className={styles.block}>
      <h3 className={styles.question}>{question.question}</h3>
      {question.hint && <p className={styles.hint}>{question.hint}</p>}
      {fields.length > 0 && (
        <div className={question.layout === "two" ? styles.bodyTwo : styles.body}>
          {fields.map((f) => (
            <QuestionField
              key={f.key}
              field={f}
              answers={answers}
              onChange={onChange}
              ocrFilledKeys={ocrFilledKeys}
            />
          ))}
        </div>
      )}
      {showFollowUp && question.followUpFields && question.followUpFields.length > 0 && (
        <div className={styles.reveal}>
          {question.followUpFields.map((f) => (
            <QuestionField
              key={f.key}
              field={f}
              answers={answers}
              onChange={onChange}
              ocrFilledKeys={ocrFilledKeys}
            />
          ))}
        </div>
      )}
      {children && <div className={styles.slot}>{children}</div>}
    </section>
  );
};

export default QuestionBlock;
